<template>
  <div class="content-container">
    <search-comp @onSearch="onSearch" @onReset="onResetSearch">
      <div class="das-form-container">
        <div class="das-form-group">
          <div class="das-form-row">
            <div class="das-form-col col-4">
              <div class="das-form-input">
                <div class="label">Khoảng ngày</div>
                <CalendarRange v-model="searchParams.date" mode="date" />
              </div>
            </div>
            <div class="das-form-col col-4">
              <div class="das-form-input">
                <div class="label">Khách sạn</div>
                <el-select v-model="searchParams.hotelIdList" multiple collapse-tags>
                  <el-option
                    v-for="hotel in hotelList"
                    :key="hotel.id"
                    :value="hotel.id"
                    :label="hotel.name"
                  />
                </el-select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </search-comp>

    <div v-for="chart in charts" :key="chart.key" class="content-box">
      <div class="section-box outline">
        <div class="das-form-container">
          <div class="das-form-title-container" style="display: flex">
            <div class="das-form-title">
              <span class="main-title">
                Biểu đồ công suất phòng của {{ chart.hotel }} — Trung bình tỷ lệ chung: {{ chart.average }}%
              </span>
            </div>
            <DownloadBtn :show-note-btn="false" />
          </div>
          <hr />
          <div style="overflow-x: auto">
            <vchart
              class="chart"
              :style="{ height: chart.height + 'px', minWidth: chart.minWidth + 'px', width: '100%' }"
              :option="chart.option"
              autoresize
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DownloadBtn from './components/DownloadBtn.vue'
import CalendarRange from '@/components/CalendarSelect/CalendarRange.vue'
import { getAllHotel } from '@/api/hotel'
import { getRoomOccupiedChart } from '@/api/report_service/datntReport'
import { HeatmapChart, LineChart } from 'echarts/charts'
import { VisualMapComponent } from 'echarts/components'
import { use } from 'echarts/core'

use([HeatmapChart, LineChart, VisualMapComponent])

const getRatios = item => item.occupied_ratios || item.occupiedRatios || []
const validRatio = value => value == null || value === '' || !Number.isFinite(Number(value))
  ? null
  : Number(value)
const escapeHtml = value => String(value).replace(/[&<>"']/g, char => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
})[char])

function makeChart(hotel, dates, index) {
  const levels = hotel.infoList || []
  const all = levels.find(level => String(level.room_level_id) === '0' || level.room_level_name === 'ALL')
  const rooms = levels.filter(level => level !== all && level.room_level_name !== 'ALL' && String(level.room_level_id) !== '0')
  const allRatios = dates.map((_, day) => validRatio(all && getRatios(all)[day]))
  const availableRatios = allRatios.filter(ratio => ratio !== null)
  const average = availableRatios.length
    ? (availableRatios.reduce((sum, ratio) => sum + ratio, 0) / availableRatios.length).toFixed(2)
    : '0.00'
  const heatmapData = []
  rooms.forEach((room, roomIndex) => {
    const ratios = getRatios(room)
    dates.forEach((_, dayIndex) => {
      const ratio = validRatio(ratios[dayIndex])
      if (ratio !== null) heatmapData.push([dayIndex, roomIndex, ratio])
    })
  })
  const heatmapHeight = Math.max(34, rooms.length * 34)

  return {
    key: `${hotel.hotelName}-${index}`,
    hotel: hotel.hotelName,
    average,
    height: 270 + heatmapHeight,
    minWidth: Math.max(800, dates.length * 32),
    option: {
      title: { text: 'Tỷ lệ chung (%)', left: 180, top: 0, textStyle: { fontSize: 13 } },
      tooltip: {
        trigger: 'item',
        formatter: params => {
          if (params.seriesType === 'line') {
            return `${escapeHtml(dates[params.dataIndex])}<br>Tỷ lệ chung: ${Number(params.value).toFixed(2)}%`
          }
          const [dayIndex, roomIndex, ratio] = params.value
          return `${escapeHtml(dates[dayIndex])}<br>${escapeHtml(rooms[roomIndex].room_level_name)}: ${ratio.toFixed(2)}%`
        }
      },
      grid: [
        { left: 180, right: 25, top: 32, height: 100 },
        { left: 180, right: 25, top: 170, height: heatmapHeight }
      ],
      xAxis: [
        { type: 'category', data: dates, boundaryGap: true, gridIndex: 0, axisLabel: { show: false }, axisTick: { show: false } },
        { type: 'category', data: dates, boundaryGap: true, gridIndex: 1, axisLabel: { rotate: 45, hideOverlap: true }, axisTick: { show: false } }
      ],
      yAxis: [
        { type: 'value', min: 0, max: 100, gridIndex: 0, axisLabel: { formatter: '{value}%' } },
        { type: 'category', data: rooms.map(room => room.room_level_name), inverse: true, gridIndex: 1, axisTick: { show: false } }
      ],
      visualMap: {
        type: 'piecewise',
        seriesIndex: 1,
        orient: 'horizontal',
        left: 'center',
        bottom: 8,
        selectedMode: false,
        pieces: [
          { gte: 0, lt: 25, label: '<25%', color: '#dbeafe' },
          { gte: 25, lt: 50, label: '25–<50%', color: '#93c5fd' },
          { gte: 50, lt: 75, label: '50–<75%', color: '#3b82f6' },
          { gte: 75, label: '≥75%', color: '#1d4ed8' }
        ]
      },
      series: [
        {
          name: 'Tỷ lệ chung',
          type: 'line',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: allRatios,
          connectNulls: false,
          symbolSize: 6,
          lineStyle: { width: 3, color: '#1d4ed8' },
          itemStyle: { color: '#1d4ed8' }
        },
        {
          name: 'Hạng phòng',
          type: 'heatmap',
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: heatmapData,
          itemStyle: { borderColor: '#fff', borderWidth: 1 },
          emphasis: { itemStyle: { borderColor: '#333', borderWidth: 1 } }
        }
      ]
    }
  }
}

export default {
  components: { DownloadBtn, CalendarRange },
  data() {
    const date = {
      start: this.$moment().subtract(30, 'days').format('YYYY-MM-DD'),
      end: this.$moment().format('YYYY-MM-DD')
    }
    return {
      searchParams: { date: { ...date }, hotelIdList: [] },
      defaultSearchParams: { date, hotelIdList: [] },
      hotelList: [],
      charts: []
    }
  },
  async mounted() {
    await this.getListHotel()
    await this.fnGetRoomOccupiedChart()
  },
  methods: {
    onSearch() {
      this.fnGetRoomOccupiedChart()
    },
    onResetSearch() {
      this.searchParams = {
        date: { ...this.defaultSearchParams.date },
        hotelIdList: [...this.defaultSearchParams.hotelIdList]
      }
      this.fnGetRoomOccupiedChart()
    },
    async fnGetRoomOccupiedChart() {
      try {
        const query = {
          dateFrom: this.searchParams.date.start,
          dateTo: this.searchParams.date.end,
          hotelIdList: this.searchParams.hotelIdList
        }
        this.charts = []
        const res = await getRoomOccupiedChart(query)
        const dates = res.date || []
        this.charts = (res.data || []).map((hotel, index) => makeChart(hotel, dates, index))
      } catch (error) {
        this.$message({
          type: 'error',
          message: this.$t('errorMsg.' + error?.response?.data?.message)
        })
      }
    },
    async getListHotel() {
      const data = await getAllHotel()
      this.hotelList = data.data
    }
  }
}
</script>
